import { FC, useState } from "react";
import {
  Button,
  ButtonGroup,
  Divider,
  Stack,
  ModalBody,
  ModalFooter,
  Text,
  useRadioGroup,
  VStack, Icon
} from "@chakra-ui/react";
import { AssetTypes, DraftAsset } from "lib/types";
import { equals, ifElse, map } from 'ramda';
import { capitalizeOrNull, uppercaseOrNull } from "lib/utils/string";
import { AlertError, RadioCard } from "components";

// TODO: Sort out the icon library
import { HtmlIcon } from "lib/icons/html";
import { FontIcon } from "lib/icons/font";
import { FileIcon } from "lib/icons/file";
import { BsLink45Deg } from "@react-icons/all-files/bs/BsLink45Deg";
import { useCreateAsset } from "lib/hooks/useCreateAsset";

type Props = {
  onClose: () => void;
  asset: DraftAsset;
}

type State = {
  value: TypeofPromptAsset;
  error: string;
  isPending: boolean;
}

export type TypeofPromptAsset = AssetTypes.LINK | AssetTypes.HTML | AssetTypes.FILE | AssetTypes.TEXT;

// TODO: Refactor, once icon lib is tidied this becomes redundant
const iconComponents: Record<TypeofPromptAsset, FC> = {
  [AssetTypes.TEXT]: FontIcon,
  [AssetTypes.HTML]: HtmlIcon,
  // eslint-disable-next-line react/display-name
  [AssetTypes.LINK]: () => <Icon as={BsLink45Deg} color={"brand.500"} />,
  [AssetTypes.FILE]: FileIcon,
};

// TODO: Refactor the icons as they are a mess
// This should be in there
const getIcon = (value: TypeofPromptAsset, iconSet: Record<TypeofPromptAsset, FC>): FC => {
  const Icon = iconSet[value];

  if (!Icon) {
    console.warn(`[getIcon]: Missing icon for ${value}`);
    // Return an FC so we don't break anything
    return () => null;
  }

  return Icon;
}

const options: TypeofPromptAsset[] = [AssetTypes.TEXT, AssetTypes.LINK, AssetTypes.FILE, AssetTypes.HTML];
export const formatPromptLabel = ifElse(equals(AssetTypes.HTML), uppercaseOrNull, capitalizeOrNull);

export const DraftPromptForm: FC<Props> = ({ onClose }) => {
  const [ state, setState ] = useState<State>({
    value: AssetTypes.TEXT,
    error: '',
    isPending: false,
  });
  const { prompt } = useCreateAsset();

  const onChange = (nextValue: TypeofPromptAsset): void => {
    setState({ ...state, value: nextValue, error: '' });
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'prompt_for',
    defaultValue: AssetTypes.TEXT,
    onChange,
  });

  const group = getRootProps();

  const createAsset = async (): Promise<void> => {
    if (!state.value) {
      setState({ ...state, error: "Select a dropzone type" });
      return;
    }

    setState({ ...state, isPending: true, error: null });
    await prompt(state.value);
    onClose();
  }

  return (
    <ModalBody>
      <VStack align={"start"} spacing={5}>
        { state.error ? <AlertError title={"Error!"} message={state.error} /> : null}
        <Text variant={"primary"} size={"sm"}>
          When you need someone to give you exact information or files, create a dropzone and give it a label.
        </Text>
        <Divider />
        <VStack align={"start"} spacing={3} width={"100%"}>
          <Text fontWeight={600} fontSize={"sm"}>Select dropzone type</Text>
          <Stack {...group} direction={['column', 'row']} width={"100%"}>
            {map((value) => {
              const radio = getRadioProps({value});
              return (
                <RadioCard
                  key={value}
                  {...radio}
                  value={value}
                  icon={getIcon(value, iconComponents)}
                >
                  {formatPromptLabel(value)}
                </RadioCard>
              )
            }, options)};
          </Stack>
        </VStack>
        <Divider />
      </VStack>
      <ModalFooter px={0}>
        <ButtonGroup width="100%" justifyContent="end">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={"primary"} isLoading={state.isPending} onClick={createAsset}>Add dropzone</Button>
        </ButtonGroup>
      </ModalFooter>
    </ModalBody>
  )
};
